import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { notification, Grid, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import moment from "moment";

import { Loading } from "components";
import {
  SendCompleteIcon,
  DownloadIcon,
  LeftPaginateArrow,
  RightPaginateArrow,
  ToggleCollapseIcon,
  PopUpIcon,
} from "icons";
import { generateCSV, localStorageHelper } from "utils";
import { useSmartLoading, useOverflowDetector, useScrollShadows } from "hooks";
import {
  GetDashboardInfoActionCreators,
  GetPatientsActionCreators,
  // GetConsultationsActionCreators,
} from "redux/rootActions";

import {
  selectIsLoadingDashboardInfo,
  selectDashboardInfoErrorMessage,
  dashboardInfoResponse,
} from "redux/dashboard/dashboardInfo/dashboardInfoStates";
import { settingInformationDataResponse } from "redux/setting/general/generalSettingStates";
import { patientListDataResponse } from "redux/patients/patientList/patientListStates";
// import { consultationListDataResponse } from "redux/consultations/consultationList/consultationListStates";

import {
  ItemTab,
  Title,
  WrapperTab,
  WrapperTabs,
  WrapperDashboardContent,
  WrapperPagination,
  YearMonthPagination,
  PaginationItem,
  ContentTableLeft2,
  ContentTableRight2,
  TableContent,
  TitlePopupWrapper2,
  TableWrapper,
  LeftTableLineItem,
  RightTableLineItem2,
  RightTableLastLineItem2,
  TableLoadingWrapper,
  TableContent2,
  TableWrapperRef,
  RightTableLineItem3,
  RightTableLastLineItem3,
  TableContent3,
} from "./Dashboard.style";
import { LoadingWrapper } from "screens/userList/UserList.style";
import InfoTooltip from "./components/InfoTooltip";

const { useBreakpoint } = Grid;
// md 768

const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const availableMonths = [7, 8, 9, 10, 11, 12];
const DASHBOARD_LAYOUT_HEADER_OFFSET = 60;
const REGISTRATION_TAB_DISPLAY_FROM = moment().startOf("day");
const CONSULTATION_TAB_DISPLAY_FROM = moment("2026-06-16").startOf("day");
const HITACHI_CITY_ID = 298;
const HITACHI_CITY_DATE_LIMIT = 16;
const HITACHI_CITY_DATE_LIMIT_MONTH = 6;
const HITACHI_CITY_DATE_LIMIT_YEAR = 2026;
const HITACHI_CITY_DATE_LIMIT_DAY = 31;

const Tabs: {
  key: number;
  name: string;
}[] = [
  {
    name: "登録者数",
    key: 1,
  },
  {
    name: "相談数",
    key: 2,
  },
];

const years: { id: number; value: number }[] = [];
for (let i = moment().year(); i >= 2025; i--) {
  years.push({
    id: i,
    value: i,
  });
}

const Dashboard4 = () => {
  const screens = useBreakpoint();
  const dispatch = useDispatch();

  const tableRef1 = useRef<any>(null);
  const tableRef2 = useRef<any>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const compareTableRef = useRef<HTMLDivElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);
  const leftHeaderRef = useRef<HTMLDivElement>(null);

  const compareTableRefRight = useRef<HTMLDivElement>(null);
  const overflowRefRight = useRef<HTMLDivElement>(null);
  const rightHeaderRef = useRef<HTMLDivElement>(null);

  const isLoadingDashboard = useSelector(selectIsLoadingDashboardInfo);
  const dashboardError = useSelector(selectDashboardInfoErrorMessage);
  const dashboardData = useSelector(dashboardInfoResponse);
  const settingInformation = useSelector(settingInformationDataResponse);
  const patientListData = useSelector(patientListDataResponse);
  // const consultationListData = useSelector(consultationListDataResponse);

  const [dashboardInfo, setDashboardInfo] = useState<any>(undefined);
  const [tab, setTab] = useState(1);
  const [collapsedYears, setCollapsedYears] = useState<Set<number>>(
    new Set([moment().year()])
  );

  const loadingDashboard = useSmartLoading(isLoadingDashboard);
  const isOverflowingLeft = useOverflowDetector(containerRef, compareTableRef, {
    direction: "horizontal",
    paddingAware: true,
  });
  const isOverflowingRight = useOverflowDetector(
    containerRef,
    compareTableRefRight,
    {
      direction: "horizontal",
      paddingAware: true,
    }
  );

  const shadow = useScrollShadows(overflowRef, tab);
  const shadowRight = useScrollShadows(overflowRefRight, tab);

  const isHouseHoldBased = useMemo<boolean>(() => {
    return settingInformation?.result?.super_city_condition?.is_household_based;
  }, [settingInformation]);
  const isOnlineConsultation = useMemo<boolean>(() => {
    return dashboardData?.result?.has_online_medical;
  }, [dashboardData]);
  const isHouseholdBasedConsultation = useMemo<boolean>(() => {
    return (
      dashboardData?.result?.has_consultation_service_type &&
      dashboardData?.result?.has_consultation_household_based
    );
  }, [dashboardData]);
  const isHouseholdBasedOnlineMedical = useMemo<boolean>(() => {
    return (
      dashboardData?.result?.has_online_medical_service_type &&
      dashboardData?.result?.has_online_medical_household_based
    );
  }, [dashboardData]);

  const [currentYear, setCurrentYear] = useState<number>(moment().year());
  const [currentMonth, setCurrentMonth] = useState<number>(
    moment().month() + 1
  );

  const totalUsers = useMemo<number>(() => {
    return patientListData?.total_record || 0;
  }, [patientListData]);

  const totalPatients = useMemo<number>(() => {
    return patientListData?.result?.total_patients || 0;
  }, [patientListData]);
  // const totalConsultationsNotCancel = useMemo<number>(() => {
  //   return consultationListData?.total_not_cancel || 0;
  // }, [consultationListData]);

  const superCityName = localStorageHelper.getItem("supercity_name");
  const superCityId = localStorageHelper.getItem("super_city_id");
  const isHitachiCity = Number(superCityId) === HITACHI_CITY_ID;

  const dateLimit = useMemo<number>(() => {
    if (
      isHitachiCity &&
      (currentYear < HITACHI_CITY_DATE_LIMIT_YEAR ||
        (currentYear === HITACHI_CITY_DATE_LIMIT_YEAR &&
          currentMonth <= HITACHI_CITY_DATE_LIMIT_MONTH))
    ) {
      if (
        currentYear < HITACHI_CITY_DATE_LIMIT_YEAR ||
        (currentYear === HITACHI_CITY_DATE_LIMIT_YEAR &&
          currentMonth < HITACHI_CITY_DATE_LIMIT_MONTH)
      ) {
        return HITACHI_CITY_DATE_LIMIT_DAY;
      } else if (
        currentYear === HITACHI_CITY_DATE_LIMIT_YEAR &&
        currentMonth === HITACHI_CITY_DATE_LIMIT_MONTH
      ) {
        return HITACHI_CITY_DATE_LIMIT;
      } else return 0;
    }
    return 0;
  }, [isHitachiCity, currentMonth, currentYear]);

  const returnDashboardData = useCallback(
    (day: number) => {
      if (!dashboardData?.result?.daily_stats?.length) return undefined;
      const data = dashboardData?.result?.daily_stats?.find(
        (item: any) => item.date === day
      );
      return data;
    },
    [dashboardData]
  );

  const returnTotalPatient = (patients: any) => {
    if (!patients) return "-";
    return patients?.gender_0 + patients?.gender_1 + patients?.gender_2;
  };

  const returnTotalHouseholdBased = (
    consult: any,
    online: any,
    consultAvailable: boolean,
    onlineAvailable: boolean
  ) => {
    if (!consultAvailable && !onlineAvailable) return "-";
    if (consultAvailable && !onlineAvailable) {
      if (consult || consult === 0) return consult;
      return "-";
    }
    if (!consultAvailable && onlineAvailable) {
      if (online || online === 0) return online;
      return "-";
    }
    if (consultAvailable && onlineAvailable) {
      if ((consult || consult === 0) && !online) return consult;
      if (!consult && (online || online === 0)) return online;
      if ((consult || consult === 0) && (online || online === 0))
        return consult + online;
      return "-";
    }
  };

  const returnTotalGender = useCallback(
    (day: number) => {
      const data = returnDashboardData(day);
      return (
        data?.breakdown?.patient?.gender_0 +
        data?.breakdown?.patient?.gender_1 +
        data?.breakdown?.patient?.gender_2
      );
    },
    [dashboardData]
  );

  // Utility function to calculate sum of gender values across all days
  const calculateGenderSum = useCallback(
    (
      dataArray: any[],
      path: string,
      genderType:
        | "gender_0"
        | "gender_1"
        | "gender_2"
        | "total"
        | "cancelled_consultations"
    ): number => {
      if (!dataArray || !Array.isArray(dataArray)) return 0;

      const shouldApplyDateLimit =
        dateLimit > 0 && path.startsWith("consultations");

      if (shouldApplyDateLimit) {
        dataArray = dataArray.filter((item: any) => item.date > dateLimit);
      }

      return dataArray.reduce((sum, item) => {
        // Navigate to the nested path (e.g., 'breakdown.patient', 'consultation.eligible', etc.)
        const pathParts = path.split(".");
        let current = item;

        for (const part of pathParts) {
          if (current && typeof current === "object" && part in current) {
            current = current[part];
          } else {
            return sum; // Path doesn't exist, skip this item
          }
        }

        // Add the gender value if it exists
        if (current && typeof current === "object" && genderType in current) {
          return sum + (current[genderType] || 0);
        }

        return sum;
      }, 0);
    },
    [dateLimit]
  );

  const returnColor = (val: any) => {
    if ((!val && val !== 0) || (val && val === "-")) return "#3B3B3B";
    if (val === 0) return "#CCCCCC";
    return "#3B3B3B";
  };

  const returnFontWeight = (val: any) => {
    if (!val) return 400;
    return 500;
  };

  const calculateTotalGender = (
    val1: number | string,
    val2: number | string,
    val3: number | string
  ): number | string => {
    if (val1 === "-" && val2 === "-" && val3 === "-") return "-";

    const parseNum = (val: number | string) =>
      val === "-" ? 0 : Number(val) || 0;

    return parseNum(val1) + parseNum(val2) + parseNum(val3);
  };

  // Example usage functions for common calculations
  const calculatePatientGenderSum = useCallback(
    (genderType: "gender_0" | "gender_1" | "gender_2"): number => {
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "breakdown.patient",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const calculatePatientTotalSum = useCallback(
    (
      genderType: "gender_0" | "gender_1" | "gender_2" | "total" | "total_users"
    ): number => {
      if (!dashboardData?.result?.daily_stats?.length) return 0;

      return dashboardData.result.daily_stats.reduce((sum: any, item: any) => {
        if (item && typeof item === "object" && genderType in item) {
          return sum + (item[genderType] || 0);
        }

        return sum;
      }, 0);
    },
    [dashboardData]
  );

  const calculateLeaveSuperCityUsersSum = useCallback((): number => {
    if (!dashboardData?.result?.daily_stats?.length) return 0;

    return dashboardData.result.daily_stats.reduce((sum: any, item: any) => {
      if (item && typeof item === "object") {
        return sum + (item?.leave_super_city?.users || 0);
      }

      return sum;
    }, 0);
  }, [dashboardData]);

  const calculateConsultationEligibleGenderSum = useCallback(
    (genderType: "gender_0" | "gender_1" | "gender_2"): number => {
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "consultation.eligible",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const calculateLeaveSuperCityGenderSum = useCallback(
    (genderType: "gender_0" | "gender_1" | "gender_2"): number => {
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "leave_super_city.patients",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const calculateConsultationHouseholdGenderSum = useCallback(
    (
      genderType: "gender_0" | "gender_1" | "gender_2"
      // consultAvailable: boolean
    ): number => {
      // if (!consultAvailable) return 0;
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "consultation.household",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const calculateConsultationsGenderSum = useCallback(
    (
      genderType:
        | "gender_0"
        | "gender_1"
        | "gender_2"
        | "cancelled_consultations"
    ): number => {
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "consultations",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const calculateConsultationsTargetGenderSum = useCallback(
    (genderType: "gender_0" | "gender_1" | "gender_2"): number => {
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "consultations.target",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const calculateConsultationsHouseholdGenderSum = useCallback(
    (genderType: "gender_0" | "gender_1" | "gender_2"): number => {
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "consultations.household",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const calculateOnlineMedicalEligibleGenderSum = useCallback(
    (genderType: "gender_0" | "gender_1" | "gender_2"): number => {
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "online_medical.eligible",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const calculateOnlineMedicalHouseholdGenderSum = useCallback(
    (
      genderType: "gender_0" | "gender_1" | "gender_2",
      onlineAvailable: boolean
    ): number => {
      if (!onlineAvailable) return 0;
      return calculateGenderSum(
        dashboardData?.result?.daily_stats || [],
        "online_medical.household",
        genderType
      );
    },
    [dashboardData, calculateGenderSum]
  );

  const getJapaneseDayOfWeek = (dateStr: string) => {
    const days = ["日", "月", "火", "水", "木", "金", "土"];
    return days[moment(dateStr, "YYYY/MM/DD").day()];
  };

  const daysOfMonth = useMemo(() => {
    const daysInMonth = moment(
      `${currentYear}-${currentMonth}`,
      "YYYY-MM"
    ).daysInMonth();
    return Array.from({ length: daysInMonth }, (_, i) => ({
      id: i + 1,
      value: i + 1,
    }));
  }, [currentMonth, currentYear]);

  useEffect(() => {
    dispatch(
      GetPatientsActionCreators.getPatientListAction({
        is_in_super_city: false,
        from_date: "",
        to_date: "",
        page: 1,
        per_page: 1,
      })
    );
  }, []);

  // useEffect(() => {
  //   if (currentYear && currentMonth) {
  //     const formattedMonth = String(currentMonth).padStart(2, "0");

  //     const targetDate = moment(`${currentYear}-${formattedMonth}`, "YYYY-MM");

  //     if (targetDate.isValid()) {
  //       dispatch(
  //         GetConsultationsActionCreators.getConsultationListAction({
  //           page: 1,
  //           per_page: 1,
  //           from_date: targetDate.clone().startOf("month").format("YYYY-MM-DD"),
  //           to_date: targetDate.clone().endOf("month").format("YYYY-MM-DD"),
  //         })
  //       );
  //     }
  //   }
  // }, [currentYear, currentMonth]);

  useEffect(() => {
    const tables = [tableRef1.current, tableRef2.current].filter(Boolean);

    if (tables.length === 0) return;

    const handleCopy = (e: any) => {
      const selection = window.getSelection();
      if (!selection || !selection.toString().trim()) return;

      const range = selection.getRangeAt(0);
      const cloned = range.cloneContents();

      // 1️⃣ Extract rows and cells (HTML table copy)
      const rows = Array.from(cloned.querySelectorAll("tr"));
      let cleaned = "";

      const datePattern = /^\d{4}\/\d{2}\/\d{2}$/; // YYYY/MM/DD

      if (rows.length > 0) {
        cleaned = rows
          .map((row) =>
            Array.from(row.querySelectorAll("td, th"))
              .map((cell: any) => cell.innerText.trim())
              .map((cellText) => {
                if (datePattern.test(cellText)) {
                  return "\n" + cellText;
                }
                return cellText;
              })
              .join("\t")
          )
          .join("\n");
      } else {
        // Fallback: vertical text or non-table selection
        const lines = selection
          .toString()
          .split(/\r?\n+/)
          .map((l) => l.trim())
          .filter(Boolean);

        cleaned = lines
          .map((line) => (datePattern.test(line) ? "\n" + line : line))
          .join("\t");
      }

      // Remove potential double newlines
      cleaned = cleaned.replace(/\n{2,}/g, "\n").trim();

      // Replace clipboard with formatted tab/newline-separated text
      e.preventDefault();
      e.clipboardData.setData("text/plain", cleaned);
      // e.clipboardData.setData("text/html", cleaned);
    };

    tables.forEach((table) => table?.addEventListener("copy", handleCopy));

    // Cleanup
    return () => {
      tables.forEach((table) => table?.removeEventListener("copy", handleCopy));
    };
  }, [tab]);

  useEffect(() => {
    let rafId: number | null = null;

    const updateHeaderOffset = (
      headerElement: HTMLDivElement | null,
      containerElement: HTMLDivElement | null
    ) => {
      if (!headerElement || !containerElement) return;

      const containerRect = containerElement.getBoundingClientRect();
      const headerHeight = headerElement.offsetHeight;
      const maxOffset = Math.max(
        containerElement.offsetHeight - headerHeight,
        0
      );
      const offset = Math.min(
        Math.max(DASHBOARD_LAYOUT_HEADER_OFFSET - containerRect.top, 0),
        maxOffset
      );

      headerElement.style.transform = `translate3d(0, ${offset}px, 0)`;
      headerElement.style.boxShadow =
        offset > 0 ? "0 2px 8px rgba(0, 0, 0, 0.08)" : "none";
      headerElement.style.zIndex = offset > 0 ? "20" : "1";
    };

    const run = () => {
      rafId = null;
      updateHeaderOffset(leftHeaderRef.current, compareTableRef.current);
      updateHeaderOffset(rightHeaderRef.current, compareTableRefRight.current);
    };

    const requestUpdate = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(run);
    };

    requestUpdate();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);

    return () => {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      if (leftHeaderRef.current) {
        leftHeaderRef.current.style.transform = "translate3d(0, 0, 0)";
        leftHeaderRef.current.style.boxShadow = "none";
        leftHeaderRef.current.style.zIndex = "1";
      }
      if (rightHeaderRef.current) {
        rightHeaderRef.current.style.transform = "translate3d(0, 0, 0)";
        rightHeaderRef.current.style.boxShadow = "none";
        rightHeaderRef.current.style.zIndex = "1";
      }
    };
  }, [tab]);

  useEffect(() => {
    dispatch(
      GetDashboardInfoActionCreators.getDashboardInfoAction({
        year: currentYear,
        month: currentMonth,
      })
    );
  }, [currentMonth, currentYear]);

  useEffect(() => {
    if (dashboardData && !isLoadingDashboard) {
      setDashboardInfo(dashboardData);
    }
  }, [dashboardData, isLoadingDashboard]);

  useEffect(() => {
    if (dashboardError) {
      // notification.error({
      //   message: "ダッシュボード情報の取得に失敗しました",
      // });
      dispatch(GetDashboardInfoActionCreators.handleResetAction());
      setDashboardInfo(undefined);
    }
  }, [dashboardError]);

  const handleToggleYear = useCallback((yearValue: number) => {
    setCollapsedYears((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(yearValue)) {
        newSet.delete(yearValue);
      } else {
        // Close all other years first
        newSet.clear();
        newSet.add(yearValue);
      }
      return newSet;
    });
  }, []);

  const isYearCollapsed = useCallback(
    (yearValue: number) => {
      return collapsedYears.has(yearValue);
    },
    [collapsedYears]
  );

  return (
    <React.Fragment>
      <Title>ダッシュボード</Title>

      <WrapperTabs>
        <WrapperTab>
          {Tabs.map((tabItem) => (
            <ItemTab key={tabItem.key}>
              <div
                className={tab === tabItem.key ? "tab-active" : ""}
                onClick={() => {
                  setTab(tabItem.key);
                }}
              >
                {tabItem.name}
              </div>
            </ItemTab>
          ))}
        </WrapperTab>
      </WrapperTabs>

      <WrapperDashboardContent ref={containerRef}>
        <WrapperPagination>
          <LeftPaginateArrow
            style={{
              display: years?.length > 1 ? "block" : "none",
              minWidth: 24,
            }}
            onClick={() => {
              if (
                currentYear &&
                currentYear >= 2025 &&
                currentYear < moment().year()
              ) {
                handleToggleYear(currentYear + 1);
                // setCollapsedYears(new Set([currentYear + 1]));
                setCurrentYear(currentYear + 1);
                if (currentYear + 1 === moment().year()) {
                  setCurrentMonth(moment().month() + 1);
                } else {
                  setCurrentMonth(1);
                }
              }
            }}
          />
          <YearMonthPagination>
            {years.map((year: any) => (
              <PaginationItem
                key={year.id}
                collapsed={isYearCollapsed(year.value)}
              >
                <div
                  className="label"
                  onClick={() => handleToggleYear(year.value)}
                  style={{ cursor: "pointer" }}
                >
                  {year.value}
                  <ToggleCollapseIcon
                    style={{
                      transform: isYearCollapsed(year.value)
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </div>
                {isYearCollapsed(year.value) && (
                  <div className="months">
                    {year.value === 2025
                      ? availableMonths.map((monthItem: any, index: number) => (
                          <div
                            key={index}
                            className={`month-item${
                              year.value === currentYear &&
                              monthItem === currentMonth
                                ? " active"
                                : ""
                            }${
                              year.value === moment().year() &&
                              monthItem > moment().month() + 1
                                ? " disabled"
                                : ""
                            }${
                              isYearCollapsed(year.value) ? " collapsed" : ""
                            }`}
                            onClick={() => {
                              if (
                                year.value !== moment().year() ||
                                (year.value === moment().year() &&
                                  monthItem <= moment().month() + 1)
                              ) {
                                setCurrentMonth(monthItem);
                                setCurrentYear(year.value);
                              }
                            }}
                          >
                            {monthItem}
                          </div>
                        ))
                      : months.map((monthItem: any, index: number) => (
                          <div
                            key={index}
                            className={`month-item${
                              year.value === currentYear &&
                              monthItem === currentMonth
                                ? " active"
                                : ""
                            }${
                              year.value === moment().year() &&
                              monthItem > moment().month() + 1
                                ? " disabled"
                                : ""
                            }${
                              isYearCollapsed(year.value) ? " collapsed" : ""
                            }`}
                            onClick={() => {
                              if (
                                year.value !== moment().year() ||
                                (year.value === moment().year() &&
                                  monthItem <= moment().month() + 1)
                              ) {
                                setCurrentMonth(monthItem);
                                setCurrentYear(year.value);
                              }
                            }}
                          >
                            {monthItem}
                          </div>
                        ))}
                  </div>
                )}
              </PaginationItem>
            ))}
          </YearMonthPagination>
          <RightPaginateArrow
            style={{
              display: years?.length > 1 ? "block" : "none",
              minWidth: 24,
            }}
            onClick={() => {
              if (
                currentYear &&
                currentYear > 2025 &&
                currentYear <= moment().year()
              ) {
                handleToggleYear(currentYear - 1);
                setCurrentYear(currentYear - 1);
                if (currentYear === 2026) {
                  setCurrentMonth(7);
                } else {
                  setCurrentMonth(1);
                }
              }
            }}
          />
        </WrapperPagination>

        <div
          style={{
            width: "100%",
            borderTop: "1px dashed #cccccc",
            margin: "16px 0px 24px",
          }}
        ></div>

        <TableWrapper
          isHouseholdBasedConsultation={
            isHouseholdBasedConsultation || isHouseholdBasedOnlineMedical
              ? true
              : false
          }
          desktopDisplay={
            (isHouseholdBasedConsultation &&
              isHouseholdBasedOnlineMedical &&
              isOnlineConsultation) ||
            (isOnlineConsultation &&
              ((isHouseholdBasedOnlineMedical &&
                !isHouseholdBasedConsultation) ||
                (!isHouseholdBasedOnlineMedical &&
                  isHouseholdBasedConsultation))) ||
            (!isOnlineConsultation && isHouseholdBasedConsultation)
              ? true
              : false
          }
        >
          {tab === 1 ? (
            <TableContent2 isFullWidth={isOverflowingLeft}>
              <TableLoadingWrapper
                loading={loadingDashboard}
                isOverflowing={isOverflowingLeft}
                ref={overflowRef}
                showShadowRight={
                  shadow.right && isOverflowingLeft ? true : false
                }
                showShadowLeft={shadow.left && isOverflowingLeft ? true : false}
                leftTable={true}
                // style={{ overflow: "visible" }}
              >
                <TableWrapperRef ref={compareTableRef}>
                  <ContentTableLeft2
                    ref={leftHeaderRef}
                    loading={loadingDashboard}
                    isHouseholdBased={isHouseholdBasedConsultation}
                  >
                    {loadingDashboard && (
                      <LoadingWrapper
                        style={{ width: 25, height: 25, top: 100 }}
                      >
                        <Loading width="25" height="25" />
                      </LoadingWrapper>
                    )}

                    <div className="first-block">
                      日付
                      {/* <div style={{ paddingLeft: "8px" }}>{currentMonth}月</div>
                      <div></div> */}
                    </div>
                    <div className="single-block pink">
                      <div
                        style={{
                          textAlign: "center",
                        }}
                      >
                        登録者
                        {/* <InfoTooltip text="契約プラン内で行われた医療相談の件数" /> */}
                      </div>
                      <div>合計</div>
                    </div>
                    <div className="single-block blue">
                      <div
                        style={{
                          textAlign: "center",
                        }}
                      >
                        世帯数
                        {/* <InfoTooltip text="契約プラン内で行われた医療相談の件数" /> */}
                      </div>
                      <div>合計</div>
                    </div>
                    <div
                      className="second-block yellow"
                      style={{
                        borderRight: isHouseholdBasedConsultation
                          ? "1px solid #cccccc"
                          : "none",
                      }}
                    >
                      <div style={{ textAlign: "center" }}>
                        登録者内訳（対象者）
                        {/* <InfoTooltip
                          text={`対象者と世帯員を含む\n自治体参加全ユーザー数`}
                        /> */}
                      </div>
                      <div style={{ display: "flex", width: "100%" }}>
                        <div className="block-1">
                          <div style={{ display: "flex" }}>
                            <div className="col-0">
                              <div>計</div>
                            </div>
                            <div className="col-1">
                              <div>男</div>
                            </div>
                            <div className="col-2">
                              <div>女</div>
                            </div>
                            <div
                              className={
                                isHouseholdBasedConsultation ? "col-3" : "col-4"
                              }
                            >
                              <div>回答なし</div>
                            </div>
                          </div>
                        </div>
                        {/* <div className="block-3">
                                                        <div>世帯</div>
                                                        <div></div>
                                                    </div> */}
                      </div>
                    </div>
                    {/* <div className="middle-block">
                      <div>
                        世帯数
                        <InfoTooltip text="登録されている世帯の数" />
                      </div>
                    </div> */}
                    {/* <div
                      className="second-block"
                      style={{
                        borderRight: "1px solid #cccccc",
                      }}
                    >
                      <div
                        style={{
                          textAlign: "center",
                          backgroundColor: "#E0E0E0",
                        }}
                      >
                        対象者
                        <InfoTooltip
                          text={`自治体が設定した\n対象ユーザーの登録者数`}
                        />
                      </div>
                      <div style={{ display: "flex", width: "100%" }}>
                        <div className="block-1">
                          <div style={{ display: "flex" }}>
                          <div className="col-0">
                              <div>計</div>
                            </div>
                            <div className="col-1">
                              <div>男</div>
                            </div>
                            <div className="col-2">
                              <div>女</div>
                            </div>
                            <div className="col-3">
                              <div>回答なし</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div> */}
                    {isHouseholdBasedConsultation ? (
                      <div
                        className="second-block green"
                        style={{ borderRight: "none" }}
                      >
                        <div
                          style={{
                            textAlign: "center",
                            backgroundColor: "#E0E0E0",
                          }}
                        >
                          登録者内訳（世帯員）
                          {/* <InfoTooltip
                            text={`対象者と同じ世帯に登録されている\n対象者以外のメンバー数`}
                          /> */}
                        </div>
                        <div style={{ display: "flex", width: "100%" }}>
                          <div
                            className="block-1"
                            // style={{
                            //   minWidth: isHouseholdBasedOnlineMedical
                            //     ? "112px"
                            //     : "122px",
                            // }}
                          >
                            <div style={{ display: "flex" }}>
                              <div className="col-0">
                                <div>計</div>
                              </div>
                              <div
                                className="col-1"
                                style={{
                                  minWidth: "47px",
                                }}
                              >
                                <div>男</div>
                              </div>
                              <div
                                className="col-2"
                                style={{
                                  minWidth: "47px",
                                }}
                              >
                                <div>女</div>
                              </div>
                              <div
                                className="col-4"
                                style={{
                                  minWidth: 66,
                                }}
                              >
                                <div>回答なし</div>
                              </div>
                            </div>
                          </div>
                          {/* {isHouseholdBasedOnlineMedical && (
                                                            <div className="block-2">
                                                                <div style={{ display: "flex" }}>
                                                                    <div className="col-1">
                                                                        <div>男</div>
                                                                    </div>
                                                                    <div className="col-2">
                                                                        <div>女</div>
                                                                    </div>
                                                                    <div className="col-3">
                                                                        <div>回答なし</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )} */}
                        </div>
                      </div>
                    ) : (
                      <></>
                    )}
                    {/* <div
                      className="third-block third-block-2"
                      style={{ borderRight: "none" }}
                    >
                      <div
                        style={{
                          textAlign: "center",
                          backgroundColor: "#E0E0E0",
                        }}
                      >
                        退会件数
                        <InfoTooltip text="当期間内に退会したユーザー数" />
                      </div>
                      <div style={{ display: "flex", width: "100%" }}>
                        <div
                          className="block-1"
                        >
                          <div style={{ display: "flex" }}>
                            <div
                              className="col-1"
                              style={{
                                minWidth: "47px",
                              }}
                            >
                              <div>男</div>
                            </div>
                            <div
                              className="col-2"
                              style={{
                                minWidth: "47px",
                              }}
                            >
                              <div>女</div>
                            </div>
                            <div
                              className="col-3 col-3-2"
                              style={{
                                minWidth: 66,
                                borderRight: "1px solid #cccccc",
                              }}
                            >
                              <div>回答なし</div>
                            </div>
                            <div
                              className="col-3"
                              style={{
                                minWidth: "47px",
                              }}
                            >
                              <div>世帯</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div> */}
                    {/* <div
                        className="middle-block"
                        style={{ borderRight: "none" }}
                      >
                        <div>世帯数</div>
                      </div> */}
                  </ContentTableLeft2>
                  <table style={{ width: "100%" }}>
                    <tbody ref={tableRef1} style={{ width: "100%" }}>
                      {daysOfMonth.map((day) => {
                        const mask = (val: number | string) => val;

                        return (
                          <RightTableLineItem2
                            key={day.id}
                            isHouseholdBased={isHouseholdBasedConsultation}
                          >
                            <td className="width-183">
                              {`${currentYear}/${
                                currentMonth < 10
                                  ? `0${currentMonth}`
                                  : currentMonth
                              }/${
                                day.value < 10 ? `0${day.value}` : day.value
                              }`}{" "}
                              (
                              {getJapaneseDayOfWeek(
                                `${currentYear}/${currentMonth}/${day.value}`
                              )}
                              )
                            </td>
                            <td
                              className="width-72-block-1"
                              style={{
                                color: returnColor(
                                  mask(
                                    returnDashboardData(day.value)?.breakdown
                                      ?.patient
                                      ? returnTotalPatient(
                                          returnDashboardData(day.value)
                                            ?.breakdown?.patient
                                        )
                                      : "-"
                                  )
                                ),
                              }}
                            >
                              {mask(
                                returnDashboardData(day.value)?.breakdown
                                  ?.patient
                                  ? returnTotalPatient(
                                      returnDashboardData(day.value)?.breakdown
                                        ?.patient
                                    )
                                  : "-"
                              )}
                            </td>
                            {/* <td className="width-72-block-1">
                            {returnDashboardData(day.value)?.breakdown?.patient
                              ?.gender_0 ||
                              returnDashboardData(day.value)?.breakdown?.patient
                                ?.gender_0 === 0
                              ? returnDashboardData(day.value)?.breakdown
                                ?.patient?.gender_0
                              : "-"}
                          </td> */}
                            {/* <td className="width-72-block-1">
                            {returnDashboardData(day.value)?.breakdown?.patient
                              ?.gender_1 ||
                              returnDashboardData(day.value)?.breakdown?.patient
                                ?.gender_1 === 0
                              ? returnDashboardData(day.value)?.breakdown
                                ?.patient?.gender_1
                              : "-"}
                          </td> */}
                            {/* <td className="width-66-none-block-1">
                            {returnDashboardData(day.value)?.breakdown?.patient
                              ?.gender_2 ||
                              returnDashboardData(day.value)?.breakdown?.patient
                                ?.gender_2 === 0
                              ? returnDashboardData(day.value)?.breakdown
                                ?.patient?.gender_2
                              : "-"}
                          </td> */}
                            <td
                              className="width-72-block-1"
                              style={{
                                color: returnColor(
                                  mask(
                                    returnDashboardData(day.value)
                                      ?.total_users ||
                                      returnDashboardData(day.value)
                                        ?.total_users === 0
                                      ? returnDashboardData(day.value)
                                          ?.total_users
                                      : "-"
                                  )
                                ),
                              }}
                            >
                              {mask(
                                returnDashboardData(day.value)?.total_users ||
                                  returnDashboardData(day.value)
                                    ?.total_users === 0
                                  ? returnDashboardData(day.value)?.total_users
                                  : "-"
                              )}
                            </td>

                            <td
                              className="width-72-block-2"
                              style={{
                                color: returnColor(
                                  mask(
                                    calculateTotalGender(
                                      returnDashboardData(day.value)
                                        ?.consultation?.eligible?.gender_0 ||
                                        returnDashboardData(day.value)
                                          ?.consultation?.eligible?.gender_0 ===
                                          0
                                        ? returnDashboardData(day.value)
                                            ?.consultation?.eligible?.gender_0
                                        : "-",
                                      returnDashboardData(day.value)
                                        ?.consultation?.eligible?.gender_1 ||
                                        returnDashboardData(day.value)
                                          ?.consultation?.eligible?.gender_1 ===
                                          0
                                        ? returnDashboardData(day.value)
                                            ?.consultation?.eligible?.gender_1
                                        : "-",
                                      returnDashboardData(day.value)
                                        ?.consultation?.eligible?.gender_2 ||
                                        returnDashboardData(day.value)
                                          ?.consultation?.eligible?.gender_2 ===
                                          0
                                        ? returnDashboardData(day.value)
                                            ?.consultation?.eligible?.gender_2
                                        : "-"
                                    )
                                  )
                                ),
                              }}
                            >
                              {mask(
                                calculateTotalGender(
                                  returnDashboardData(day.value)?.consultation
                                    ?.eligible?.gender_0 ||
                                    returnDashboardData(day.value)?.consultation
                                      ?.eligible?.gender_0 === 0
                                    ? returnDashboardData(day.value)
                                        ?.consultation?.eligible?.gender_0
                                    : "-",
                                  returnDashboardData(day.value)?.consultation
                                    ?.eligible?.gender_1 ||
                                    returnDashboardData(day.value)?.consultation
                                      ?.eligible?.gender_1 === 0
                                    ? returnDashboardData(day.value)
                                        ?.consultation?.eligible?.gender_1
                                    : "-",
                                  returnDashboardData(day.value)?.consultation
                                    ?.eligible?.gender_2 ||
                                    returnDashboardData(day.value)?.consultation
                                      ?.eligible?.gender_2 === 0
                                    ? returnDashboardData(day.value)
                                        ?.consultation?.eligible?.gender_2
                                    : "-"
                                )
                              )}
                              {/* {returnDashboardData(day.value)?.consultation
                              ?.eligible?.gender_0 ||
                              returnDashboardData(day.value)?.consultation
                                ?.eligible?.gender_0 === 0
                              ? returnDashboardData(day.value)?.consultation
                                ?.eligible?.gender_0
                              : "-"} */}
                            </td>
                            <td
                              className="width-72-block-2"
                              style={{
                                color: returnColor(
                                  mask(
                                    returnDashboardData(day.value)?.consultation
                                      ?.eligible?.gender_0 ||
                                      returnDashboardData(day.value)
                                        ?.consultation?.eligible?.gender_0 === 0
                                      ? returnDashboardData(day.value)
                                          ?.consultation?.eligible?.gender_0
                                      : "-"
                                  )
                                ),
                              }}
                            >
                              {mask(
                                returnDashboardData(day.value)?.consultation
                                  ?.eligible?.gender_0 ||
                                  returnDashboardData(day.value)?.consultation
                                    ?.eligible?.gender_0 === 0
                                  ? returnDashboardData(day.value)?.consultation
                                      ?.eligible?.gender_0
                                  : "-"
                              )}
                            </td>
                            <td
                              className="width-72-block-2"
                              style={{
                                color: returnColor(
                                  mask(
                                    returnDashboardData(day.value)?.consultation
                                      ?.eligible?.gender_1 ||
                                      returnDashboardData(day.value)
                                        ?.consultation?.eligible?.gender_1 === 0
                                      ? returnDashboardData(day.value)
                                          ?.consultation?.eligible?.gender_1
                                      : "-"
                                  )
                                ),
                              }}
                            >
                              {mask(
                                returnDashboardData(day.value)?.consultation
                                  ?.eligible?.gender_1 ||
                                  returnDashboardData(day.value)?.consultation
                                    ?.eligible?.gender_1 === 0
                                  ? returnDashboardData(day.value)?.consultation
                                      ?.eligible?.gender_1
                                  : "-"
                              )}
                            </td>
                            <td
                              className="width-66-none-block-3"
                              style={{
                                color: returnColor(
                                  mask(
                                    returnDashboardData(day.value)?.consultation
                                      ?.eligible?.gender_2 ||
                                      returnDashboardData(day.value)
                                        ?.consultation?.eligible?.gender_2 === 0
                                      ? returnDashboardData(day.value)
                                          ?.consultation?.eligible?.gender_2
                                      : "-"
                                  )
                                ),
                                borderRight: isHouseholdBasedConsultation
                                  ? "1px solid #cccccc"
                                  : "none",
                              }}
                            >
                              {mask(
                                returnDashboardData(day.value)?.consultation
                                  ?.eligible?.gender_2 ||
                                  returnDashboardData(day.value)?.consultation
                                    ?.eligible?.gender_2 === 0
                                  ? returnDashboardData(day.value)?.consultation
                                      ?.eligible?.gender_2
                                  : "-"
                              )}
                            </td>

                            {/* {isHouseholdBasedConsultation && (
                                                      
                                                  )} */}
                            {isHouseholdBasedConsultation && (
                              <>
                                <td
                                  className="width-72-block-2"
                                  style={{
                                    color: returnColor(
                                      mask(
                                        calculateTotalGender(
                                          returnDashboardData(day.value)
                                            ?.consultation?.household
                                            ?.gender_0 ||
                                            returnDashboardData(day.value)
                                              ?.consultation?.household
                                              ?.gender_0 === 0
                                            ? returnDashboardData(day.value)
                                                ?.consultation
                                                ?.is_household_based
                                              ? returnDashboardData(day.value)
                                                  ?.consultation?.household
                                                  ?.gender_0
                                              : "-"
                                            : "-",
                                          returnDashboardData(day.value)
                                            ?.consultation?.household
                                            ?.gender_1 ||
                                            returnDashboardData(day.value)
                                              ?.consultation?.household
                                              ?.gender_1 === 0
                                            ? returnDashboardData(day.value)
                                                ?.consultation
                                                ?.is_household_based
                                              ? returnDashboardData(day.value)
                                                  ?.consultation?.household
                                                  ?.gender_1
                                              : "-"
                                            : "-",
                                          returnDashboardData(day.value)
                                            ?.consultation?.household
                                            ?.gender_2 ||
                                            returnDashboardData(day.value)
                                              ?.consultation?.household
                                              ?.gender_2 === 0
                                            ? returnDashboardData(day.value)
                                                ?.consultation
                                                ?.is_household_based
                                              ? returnDashboardData(day.value)
                                                  ?.consultation?.household
                                                  ?.gender_2
                                              : "-"
                                            : "-"
                                        )
                                      )
                                    ),
                                  }}
                                >
                                  {mask(
                                    calculateTotalGender(
                                      returnDashboardData(day.value)
                                        ?.consultation?.household?.gender_0 ||
                                        returnDashboardData(day.value)
                                          ?.consultation?.household
                                          ?.gender_0 === 0
                                        ? returnDashboardData(day.value)
                                            ?.consultation?.is_household_based
                                          ? returnDashboardData(day.value)
                                              ?.consultation?.household
                                              ?.gender_0
                                          : "-"
                                        : "-",
                                      returnDashboardData(day.value)
                                        ?.consultation?.household?.gender_1 ||
                                        returnDashboardData(day.value)
                                          ?.consultation?.household
                                          ?.gender_1 === 0
                                        ? returnDashboardData(day.value)
                                            ?.consultation?.is_household_based
                                          ? returnDashboardData(day.value)
                                              ?.consultation?.household
                                              ?.gender_1
                                          : "-"
                                        : "-",
                                      returnDashboardData(day.value)
                                        ?.consultation?.household?.gender_2 ||
                                        returnDashboardData(day.value)
                                          ?.consultation?.household
                                          ?.gender_2 === 0
                                        ? returnDashboardData(day.value)
                                            ?.consultation?.is_household_based
                                          ? returnDashboardData(day.value)
                                              ?.consultation?.household
                                              ?.gender_2
                                          : "-"
                                        : "-"
                                    )
                                  )}
                                  {/* {returnDashboardData(day.value)?.consultation
                                  ?.household?.gender_0 ||
                                  returnDashboardData(day.value)?.consultation
                                    ?.household?.gender_0 === 0
                                  ? returnDashboardData(day.value)?.consultation
                                    ?.is_household_based
                                    ? returnDashboardData(day.value)
                                      ?.consultation?.household?.gender_0
                                    : "-"
                                  : "-"} */}
                                </td>
                                <td
                                  className="width-72-block-2"
                                  style={{
                                    color: returnColor(
                                      mask(
                                        returnDashboardData(day.value)
                                          ?.consultation?.household?.gender_0 ||
                                          returnDashboardData(day.value)
                                            ?.consultation?.household
                                            ?.gender_0 === 0
                                          ? returnDashboardData(day.value)
                                              ?.consultation?.is_household_based
                                            ? returnDashboardData(day.value)
                                                ?.consultation?.household
                                                ?.gender_0
                                            : "-"
                                          : "-"
                                      )
                                    ),
                                  }}
                                >
                                  {mask(
                                    returnDashboardData(day.value)?.consultation
                                      ?.household?.gender_0 ||
                                      returnDashboardData(day.value)
                                        ?.consultation?.household?.gender_0 ===
                                        0
                                      ? returnDashboardData(day.value)
                                          ?.consultation?.is_household_based
                                        ? returnDashboardData(day.value)
                                            ?.consultation?.household?.gender_0
                                        : "-"
                                      : "-"
                                  )}
                                </td>
                                <td
                                  className="width-72-block-2"
                                  style={{
                                    color: returnColor(
                                      mask(
                                        returnDashboardData(day.value)
                                          ?.consultation?.household?.gender_1 ||
                                          returnDashboardData(day.value)
                                            ?.consultation?.household
                                            ?.gender_1 === 0
                                          ? returnDashboardData(day.value)
                                              ?.consultation?.is_household_based
                                            ? returnDashboardData(day.value)
                                                ?.consultation?.household
                                                ?.gender_1
                                            : "-"
                                          : "-"
                                      )
                                    ),
                                  }}
                                >
                                  {mask(
                                    returnDashboardData(day.value)?.consultation
                                      ?.household?.gender_1 ||
                                      returnDashboardData(day.value)
                                        ?.consultation?.household?.gender_1 ===
                                        0
                                      ? returnDashboardData(day.value)
                                          ?.consultation?.is_household_based
                                        ? returnDashboardData(day.value)
                                            ?.consultation?.household?.gender_1
                                        : "-"
                                      : "-"
                                  )}
                                </td>
                                <td
                                  className="width-66-none-block-2"
                                  style={{
                                    borderRight: "none",
                                    color: returnColor(
                                      mask(
                                        returnDashboardData(day.value)
                                          ?.consultation?.household?.gender_2 ||
                                          returnDashboardData(day.value)
                                            ?.consultation?.household
                                            ?.gender_2 === 0
                                          ? returnDashboardData(day.value)
                                              ?.consultation?.is_household_based
                                            ? returnDashboardData(day.value)
                                                ?.consultation?.household
                                                ?.gender_2
                                            : "-"
                                          : "-"
                                      )
                                    ),
                                  }}
                                >
                                  {mask(
                                    returnDashboardData(day.value)?.consultation
                                      ?.household?.gender_2 ||
                                      returnDashboardData(day.value)
                                        ?.consultation?.household?.gender_2 ===
                                        0
                                      ? returnDashboardData(day.value)
                                          ?.consultation?.is_household_based
                                        ? returnDashboardData(day.value)
                                            ?.consultation?.household?.gender_2
                                        : "-"
                                      : "-"
                                  )}
                                </td>
                                {/* {isHouseholdBasedOnlineMedical && (
                                                                <>
                                                                    <td className="width-47">
                                                                        {returnDashboardData(day.value)
                                                                            ?.online_medical?.is_available &&
                                                                            returnDashboardData(day.value)?.online_medical
                                                                                ?.is_household_based
                                                                            ? returnDashboardData(day.value)
                                                                                ?.online_medical?.household?.gender_0 ||
                                                                            0
                                                                            : "-"}
                                                                    </td>
                                                                    <td className="width-47">
                                                                        {returnDashboardData(day.value)
                                                                            ?.online_medical?.is_available &&
                                                                            returnDashboardData(day.value)?.online_medical
                                                                                ?.is_household_based
                                                                            ? returnDashboardData(day.value)
                                                                                ?.online_medical?.household?.gender_1 ||
                                                                            0
                                                                            : "-"}
                                                                    </td>
                                                                    <td className="width-66">
                                                                        {returnDashboardData(day.value)
                                                                            ?.online_medical?.is_available &&
                                                                            returnDashboardData(day.value)?.online_medical
                                                                                      ?.is_household_based
                                                                            ? returnDashboardData(day.value)
                                                                                ?.online_medical?.household?.gender_2 ||
                                                                            0
                                                                            : "-"}
                                                                    </td>
                                                                </>
                                                            )} */}
                              </>
                            )}
                            {/* <td className="width-72-block-3-1">
                            {returnDashboardData(day.value)?.leave_super_city
                              ?.patients?.gender_0 ||
                            returnDashboardData(day.value)?.leave_super_city
                              ?.patients?.gender_0 === 0
                              ? returnDashboardData(day.value)?.leave_super_city
                                  ?.patients?.gender_0
                              : "-"}
                          </td>
                          <td className="width-72-block-3-2">
                            {returnDashboardData(day.value)?.leave_super_city
                              ?.patients?.gender_1 ||
                            returnDashboardData(day.value)?.leave_super_city
                              ?.patients?.gender_1 === 0
                              ? returnDashboardData(day.value)?.leave_super_city
                                  ?.patients?.gender_1
                              : "-"}
                          </td>
                          <td className="width-66-none-block-3">
                            {returnDashboardData(day.value)?.leave_super_city
                              ?.patients?.gender_2 ||
                            returnDashboardData(day.value)?.leave_super_city
                              ?.patients?.gender_2 === 0
                              ? returnDashboardData(day.value)?.leave_super_city
                                  ?.patients?.gender_2
                              : "-"}
                          </td>
                          <td className="width-66">
                            {returnDashboardData(day.value)?.leave_super_city
                              ?.users ||
                            returnDashboardData(day.value)?.leave_super_city
                              ?.users === 0
                              ? returnDashboardData(day.value)?.leave_super_city
                                  ?.users
                              : "-"}
                          </td> */}
                          </RightTableLineItem2>
                        );
                      })}
                      <RightTableLastLineItem2
                        style={{ display: "none" }}
                        isHouseholdBased={isHouseholdBasedConsultation}
                      >
                        <td className="width-183">合計</td>
                        <td className="width-72-block-1">
                          {/* {calculatePatientGenderSum("gender_0") +
                            calculatePatientGenderSum("gender_1") +
                            calculatePatientGenderSum("gender_2")} */}
                        </td>
                        {/* <td className="width-72-block-1">
                          {calculatePatientGenderSum("gender_0")}
                        </td>
                        <td className="width-72-block-1">
                          {calculatePatientGenderSum("gender_1")}
                        </td>
                        <td className="width-66-none-block-1">
                          {calculatePatientGenderSum("gender_2")}
                        </td> */}
                        <td className="width-72-block-1">
                          {/* {calculatePatientTotalSum("total_users")} */}
                        </td>
                        <td className="width-72-block-2">
                          {/* {calculateTotalGender(
                            calculateConsultationEligibleGenderSum("gender_0"),
                            calculateConsultationEligibleGenderSum("gender_1"),
                            calculateConsultationEligibleGenderSum("gender_2")
                          )} */}
                          {/* {calculateConsultationEligibleGenderSum("gender_0")} */}
                        </td>
                        <td className="width-72-block-2">
                          {/* {calculateConsultationEligibleGenderSum("gender_0")} */}
                        </td>
                        <td className="width-72-block-2">
                          {/* {calculateConsultationEligibleGenderSum("gender_1")} */}
                        </td>
                        <td className="width-66-none-block-2">
                          {/* {calculateConsultationEligibleGenderSum("gender_2")} */}
                        </td>
                        {isHouseholdBasedConsultation && (
                          <>
                            <td className="width-72-block-2">
                              {/* {calculateConsultationHouseholdGenderSum(
                                "gender_0"
                              )} */}
                              {/* {calculateTotalGender(
                                calculateConsultationHouseholdGenderSum(
                                  "gender_0"
                                ),
                                calculateConsultationHouseholdGenderSum(
                                  "gender_1"
                                ),
                                calculateConsultationHouseholdGenderSum(
                                  "gender_2"
                                )
                              )} */}
                            </td>
                            <td className="width-72-block-2">
                              {/* {calculateConsultationHouseholdGenderSum(
                                "gender_0"
                              )} */}
                            </td>
                            <td className="width-72-block-2">
                              {/* {calculateConsultationHouseholdGenderSum(
                                "gender_1"
                              )} */}
                            </td>
                            <td
                              className="width-66-none-block-2"
                              style={{ borderRight: "none" }}
                            >
                              {/* {calculateConsultationHouseholdGenderSum(
                                "gender_2"
                              )} */}
                            </td>
                          </>
                        )}
                        {/* <td className="width-72-block-3-1">
                          {calculateLeaveSuperCityGenderSum("gender_0")}
                        </td>
                        <td className="width-72-block-3-2">
                          {calculateLeaveSuperCityGenderSum("gender_1")}
                        </td>
                        <td className="width-66-none-block-3">
                          {calculateLeaveSuperCityGenderSum("gender_2")}
                        </td>
                        <td className="width-66">
                          {calculateLeaveSuperCityUsersSum()}
                        </td> */}
                        {/* {isOnlineConsultation && (
                                                    <>
                                                        <td
                                                            className="width-47"
                                                            style={{
                                                                minWidth: "47px",
                                                            }}
                                                        >
                                                            {calculateOnlineMedicalEligibleGenderSum(
                                                                "gender_0"
                                                            )}
                                                        </td>
                                                        <td
                                                            className="width-47"
                                                            style={{
                                                                minWidth: "47px",
                                                            }}
                                                        >
                                                            {calculateOnlineMedicalEligibleGenderSum(
                                                                "gender_1"
                                                            )}
                                                        </td>
                                                        <td className="width-66">
                                                            {calculateOnlineMedicalEligibleGenderSum(
                                                                "gender_2"
                                                            )}
                                                        </td>
                                                        {isHouseholdBasedOnlineMedical && (
                                                            <>
                                                                <td className="width-47">
                                                                    {calculateOnlineMedicalHouseholdGenderSum(
                                                                        "gender_0"
                                                                    )}
                                                                </td>
                                                                <td className="width-47">
                                                                    {calculateOnlineMedicalHouseholdGenderSum(
                                                                        "gender_1"
                                                                    )}
                                                                </td>
                                                                <td className="width-66">
                                                                    {calculateOnlineMedicalHouseholdGenderSum(
                                                                        "gender_2"
                                                                    )}
                                                                </td>
                                                            </>
                                                        )}
                                                    </>
                                                )} */}
                      </RightTableLastLineItem2>
                    </tbody>
                  </table>
                </TableWrapperRef>
              </TableLoadingWrapper>
              <TitlePopupWrapper2>
                {/* <div className="title-popup">登録総数</div> */}
                <div className="title-text">
                  {moment().format("YYYY年M月D日")}時点での登録者数合計：
                  {totalPatients}人（世帯数：{totalUsers}）
                </div>
                <div
                  className={`popup-btn${
                    !dashboardData?.result?.google_sheet_urls?.patient
                      ? " disabled-btn"
                      : ""
                  }`}
                  onClick={() => {
                    if (dashboardData?.result?.google_sheet_urls?.patient) {
                      window.open(
                        dashboardData?.result?.google_sheet_urls?.patient,
                        "_blank"
                      );
                    }
                  }}
                >
                  {loadingDashboard ? (
                    <Spin
                      indicator={
                        <LoadingOutlined
                          style={{ fontSize: 20, color: "white" }}
                          spin
                        />
                      }
                      size="small"
                      style={{ margin: "2px auto 0px 51px" }}
                    />
                  ) : (
                    <>
                      <PopUpIcon />
                      <div className="name">スプレッドシート</div>
                    </>
                  )}
                </div>
              </TitlePopupWrapper2>
            </TableContent2>
          ) : (
            <></>
          )}
          {tab === 2 ? (
            <TableContent3 isFullWidth={isOverflowingRight}>
              <TableLoadingWrapper
                loading={loadingDashboard}
                isOverflowing={isOverflowingRight}
                ref={overflowRefRight}
                showShadowRight={
                  shadowRight.right && isOverflowingRight ? true : false
                }
                showShadowLeft={
                  shadowRight.left && isOverflowingRight ? true : false
                }
                // style={{ overflow: "visible" }}
              >
                <div
                  ref={compareTableRefRight}
                  // style={{ width: "max-content" }}
                >
                  <ContentTableRight2
                    ref={rightHeaderRef}
                    loading={loadingDashboard}
                  >
                    {loadingDashboard && (
                      <LoadingWrapper
                        style={{ width: 25, height: 25, top: 100 }}
                      >
                        <Loading width="25" height="25" />
                      </LoadingWrapper>
                    )}
                    <div className="first-block">
                      日付
                      {/* <div>{currentMonth}月</div>
                      <div></div> */}
                    </div>
                    <div className="single-block pink">
                      <div
                        style={{
                          textAlign: "center",
                        }}
                      >
                        相談数
                        {/* <InfoTooltip text="契約プラン内で行われた医療相談の件数" /> */}
                      </div>
                      <div>合計</div>
                    </div>
                    <div
                      className="third-block yellow"
                      style={{
                        borderRight: isHouseholdBasedConsultation
                          ? "1px solid #cccccc"
                          : "none",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#E0E0E0",
                          textAlign: "center",
                        }}
                        className="yellow"
                      >
                        相談者内訳（対象者）
                        {/* <InfoTooltip text="自治体が設定した対象ユーザーの相談数" /> */}
                        {/* 0歳〜15歳（中学3年） */}
                      </div>
                      <div style={{ display: "flex", width: "100%" }}>
                        <div className="block-1">
                          <div style={{ display: "flex" }}>
                            <div className="yellow col-1">
                              <div>男</div>
                            </div>
                            <div className="yellow col-2">
                              <div>女</div>
                            </div>
                            <div className="yellow col-3">
                              <div>回答なし</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {isHouseholdBasedConsultation && (
                      <div
                        className="third-block"
                        style={{ borderRight: "none" }}
                      >
                        <div
                          style={{
                            textAlign: "center",
                            backgroundColor: "#E0E0E0",
                          }}
                          className="green"
                        >
                          相談者内訳（世帯員）
                          {/* <InfoTooltip
                            text={`対象者と同じ世帯に登録されている\n対象者以外のメンバーの相談数`}
                          /> */}
                        </div>
                        <div style={{ display: "flex", width: "100%" }}>
                          <div className="block-1 green">
                            <div style={{ display: "flex" }}>
                              <div className="col-1">
                                <div>男</div>
                              </div>
                              <div className="col-2">
                                <div>女</div>
                              </div>
                              <div className="col-3">
                                <div>回答なし</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* <div
                      className="single-block grey"
                      style={{ borderRight: "none" }}
                    >
                      <div
                        style={{
                          textAlign: "center",
                        }}
                      >
                        キャンセル数
                      </div>
                      <div>合計</div>
                    </div> */}
                  </ContentTableRight2>
                  <table>
                    <tbody ref={tableRef2}>
                      {daysOfMonth.map((day) => {
                        const mask = (val: number | string) => {
                          const targetDate = moment(
                            `${currentYear}-${currentMonth}-${day.value}`,
                            "YYYY-M-D"
                          );

                          return isHitachiCity &&
                            targetDate.isBefore(CONSULTATION_TAB_DISPLAY_FROM)
                            ? "-"
                            : val;
                        };

                        return (
                          <RightTableLineItem3 key={day.id}>
                            <td className="width-183">
                              {`${currentYear}/${
                                currentMonth < 10
                                  ? `0${currentMonth}`
                                  : currentMonth
                              }/${
                                day.value < 10 ? `0${day.value}` : day.value
                              }`}{" "}
                              (
                              {getJapaneseDayOfWeek(
                                `${currentYear}/${currentMonth}/${day.value}`
                              )}
                              )
                            </td>
                            <td
                              className="width-72-single"
                              style={{
                                color: returnColor(
                                  mask(
                                    returnDashboardData(day.value)
                                      ?.consultations
                                      ? returnTotalPatient(
                                          returnDashboardData(day.value)
                                            ?.consultations
                                        )
                                      : "-"
                                  )
                                ),
                              }}
                            >
                              {mask(
                                returnDashboardData(day.value)?.consultations
                                  ? returnTotalPatient(
                                      returnDashboardData(day.value)
                                        ?.consultations
                                    )
                                  : "-"
                              )}
                            </td>
                            {/* <td className="width-72">
                            {returnDashboardData(day.value)?.consultations
                              ?.gender_0 ||
                              returnDashboardData(day.value)?.consultations
                                ?.gender_0 === 0
                              ? returnDashboardData(day.value)?.consultations
                                ?.gender_0
                              : "-"}
                          </td>
                          <td className="width-72">
                            {returnDashboardData(day.value)?.consultations
                              ?.gender_1 ||
                              returnDashboardData(day.value)?.consultations
                                ?.gender_1 === 0
                              ? returnDashboardData(day.value)?.consultations
                                ?.gender_1
                              : "-"}
                          </td>
                          <td className="width-66">
                            {returnDashboardData(day.value)?.consultations
                              ?.gender_2 ||
                              returnDashboardData(day.value)?.consultations
                                ?.gender_2 === 0
                              ? returnDashboardData(day.value)?.consultations
                                ?.gender_2
                              : "-"}
                          </td> */}
                            <td
                              className="width-72"
                              style={{
                                color: returnColor(
                                  mask(
                                    returnDashboardData(day.value)
                                      ?.consultations?.target?.gender_0 ||
                                      returnDashboardData(day.value)
                                        ?.consultations?.target?.gender_0 === 0
                                      ? returnDashboardData(day.value)
                                          ?.consultations?.target?.gender_0
                                      : "-"
                                  )
                                ),
                              }}
                            >
                              {mask(
                                returnDashboardData(day.value)?.consultations
                                  ?.target?.gender_0 ||
                                  returnDashboardData(day.value)?.consultations
                                    ?.target?.gender_0 === 0
                                  ? returnDashboardData(day.value)
                                      ?.consultations?.target?.gender_0
                                  : "-"
                              )}
                            </td>
                            <td
                              className="width-72"
                              style={{
                                color: returnColor(
                                  mask(
                                    returnDashboardData(day.value)
                                      ?.consultations?.target?.gender_1 ||
                                      returnDashboardData(day.value)
                                        ?.consultations?.target?.gender_1 === 0
                                      ? returnDashboardData(day.value)
                                          ?.consultations?.target?.gender_1
                                      : "-"
                                  )
                                ),
                              }}
                            >
                              {mask(
                                returnDashboardData(day.value)?.consultations
                                  ?.target?.gender_1 ||
                                  returnDashboardData(day.value)?.consultations
                                    ?.target?.gender_1 === 0
                                  ? returnDashboardData(day.value)
                                      ?.consultations?.target?.gender_1
                                  : "-"
                              )}
                            </td>
                            <td
                              className="width-66"
                              style={{
                                color: returnColor(
                                  mask(
                                    returnDashboardData(day.value)
                                      ?.consultations?.target?.gender_2 ||
                                      returnDashboardData(day.value)
                                        ?.consultations?.target?.gender_2 === 0
                                      ? returnDashboardData(day.value)
                                          ?.consultations?.target?.gender_2
                                      : "-"
                                  )
                                ),
                              }}
                            >
                              {mask(
                                returnDashboardData(day.value)?.consultations
                                  ?.target?.gender_2 ||
                                  returnDashboardData(day.value)?.consultations
                                    ?.target?.gender_2 === 0
                                  ? returnDashboardData(day.value)
                                      ?.consultations?.target?.gender_2
                                  : "-"
                              )}
                            </td>
                            {isHouseholdBasedConsultation && (
                              <>
                                <td
                                  className="width-72"
                                  style={{
                                    color: returnColor(
                                      mask(
                                        returnDashboardData(day.value)
                                          ?.consultations?.household
                                          ?.gender_0 ||
                                          returnDashboardData(day.value)
                                            ?.consultations?.household
                                            ?.gender_0 === 0
                                          ? returnDashboardData(day.value)
                                              ?.consultation?.is_household_based
                                            ? returnDashboardData(day.value)
                                                ?.consultations?.household
                                                ?.gender_0
                                            : "-"
                                          : "-"
                                      )
                                    ),
                                  }}
                                >
                                  {mask(
                                    returnDashboardData(day.value)
                                      ?.consultations?.household?.gender_0 ||
                                      returnDashboardData(day.value)
                                        ?.consultations?.household?.gender_0 ===
                                        0
                                      ? returnDashboardData(day.value)
                                          ?.consultation?.is_household_based
                                        ? returnDashboardData(day.value)
                                            ?.consultations?.household?.gender_0
                                        : "-"
                                      : "-"
                                  )}
                                </td>
                                <td
                                  className="width-72"
                                  style={{
                                    color: returnColor(
                                      mask(
                                        returnDashboardData(day.value)
                                          ?.consultations?.household
                                          ?.gender_1 ||
                                          returnDashboardData(day.value)
                                            ?.consultations?.household
                                            ?.gender_1 === 0
                                          ? returnDashboardData(day.value)
                                              ?.consultation?.is_household_based
                                            ? returnDashboardData(day.value)
                                                ?.consultations?.household
                                                ?.gender_1
                                            : "-"
                                          : "-"
                                      )
                                    ),
                                  }}
                                >
                                  {mask(
                                    returnDashboardData(day.value)
                                      ?.consultations?.household?.gender_1 ||
                                      returnDashboardData(day.value)
                                        ?.consultations?.household?.gender_1 ===
                                        0
                                      ? returnDashboardData(day.value)
                                          ?.consultation?.is_household_based
                                        ? returnDashboardData(day.value)
                                            ?.consultations?.household?.gender_1
                                        : "-"
                                      : "-"
                                  )}
                                </td>
                                <td
                                  className="width-66"
                                  style={{
                                    color: returnColor(
                                      mask(
                                        returnDashboardData(day.value)
                                          ?.consultations?.household
                                          ?.gender_2 ||
                                          returnDashboardData(day.value)
                                            ?.consultations?.household
                                            ?.gender_2 === 0
                                          ? returnDashboardData(day.value)
                                              ?.consultation?.is_household_based
                                            ? returnDashboardData(day.value)
                                                ?.consultations?.household
                                                ?.gender_2
                                            : "-"
                                          : "-"
                                      )
                                    ),
                                  }}
                                >
                                  {mask(
                                    returnDashboardData(day.value)
                                      ?.consultations?.household?.gender_2 ||
                                      returnDashboardData(day.value)
                                        ?.consultations?.household?.gender_2 ===
                                        0
                                      ? returnDashboardData(day.value)
                                          ?.consultation?.is_household_based
                                        ? returnDashboardData(day.value)
                                            ?.consultations?.household?.gender_2
                                        : "-"
                                      : "-"
                                  )}
                                </td>
                              </>
                            )}
                            {/* <td
                            className="width-126"
                            style={{
                              color: returnColor(
                                returnDashboardData(day.value)?.consultations
                                  ?.cancelled_consultations ||
                                  returnDashboardData(day.value)?.consultations
                                    ?.cancelled_consultations === 0
                                  ? returnDashboardData(day.value)
                                      ?.consultations?.cancelled_consultations
                                  : "-"
                              ),
                            }}
                          >
                            {returnDashboardData(day.value)?.consultations
                              ?.cancelled_consultations ||
                            returnDashboardData(day.value)?.consultations
                              ?.cancelled_consultations === 0
                              ? returnDashboardData(day.value)?.consultations
                                  ?.cancelled_consultations
                              : "-"}
                          </td> */}
                          </RightTableLineItem3>
                        );
                      })}
                      <RightTableLastLineItem3>
                        <td className="width-183">合計</td>
                        <td
                          className="width-72-single"
                          style={{
                            color: returnColor(
                              calculateConsultationsGenderSum("gender_0") +
                                calculateConsultationsGenderSum("gender_1") +
                                calculateConsultationsGenderSum("gender_2")
                            ),
                            fontWeight: returnFontWeight(
                              calculateConsultationsGenderSum("gender_0") +
                                calculateConsultationsGenderSum("gender_1") +
                                calculateConsultationsGenderSum("gender_2")
                            ),
                          }}
                        >
                          {calculateConsultationsGenderSum("gender_0") +
                            calculateConsultationsGenderSum("gender_1") +
                            calculateConsultationsGenderSum("gender_2")}
                        </td>
                        {/* <td className="width-72">
                          {calculateConsultationsGenderSum("gender_0")}
                        </td>
                        <td className="width-72">
                          {calculateConsultationsGenderSum("gender_1")}
                        </td>
                        <td className="width-66">
                          {calculateConsultationsGenderSum("gender_2")}
                        </td> */}
                        <td
                          className="width-72"
                          style={{
                            color: returnColor(
                              calculateConsultationsTargetGenderSum("gender_0")
                            ),
                            fontWeight: returnFontWeight(
                              calculateConsultationsTargetGenderSum("gender_0")
                            ),
                          }}
                        >
                          {calculateConsultationsTargetGenderSum("gender_0")}
                        </td>
                        <td
                          className="width-72"
                          style={{
                            color: returnColor(
                              calculateConsultationsTargetGenderSum("gender_1")
                            ),
                            fontWeight: returnFontWeight(
                              calculateConsultationsTargetGenderSum("gender_1")
                            ),
                          }}
                        >
                          {calculateConsultationsTargetGenderSum("gender_1")}
                        </td>
                        <td
                          className="width-66"
                          style={{
                            color: returnColor(
                              calculateConsultationsTargetGenderSum("gender_2")
                            ),
                            fontWeight: returnFontWeight(
                              calculateConsultationsTargetGenderSum("gender_2")
                            ),
                          }}
                        >
                          {calculateConsultationsTargetGenderSum("gender_2")}
                        </td>
                        {isHouseholdBasedConsultation && (
                          <>
                            <td
                              className="width-72"
                              style={{
                                color: returnColor(
                                  calculateConsultationsHouseholdGenderSum(
                                    "gender_0"
                                  )
                                ),
                                fontWeight: returnFontWeight(
                                  calculateConsultationsHouseholdGenderSum(
                                    "gender_0"
                                  )
                                ),
                              }}
                            >
                              {calculateConsultationsHouseholdGenderSum(
                                "gender_0"
                              )}
                            </td>
                            <td
                              className="width-72"
                              style={{
                                color: returnColor(
                                  calculateConsultationsHouseholdGenderSum(
                                    "gender_1"
                                  )
                                ),
                                fontWeight: returnFontWeight(
                                  calculateConsultationsHouseholdGenderSum(
                                    "gender_1"
                                  )
                                ),
                              }}
                            >
                              {calculateConsultationsHouseholdGenderSum(
                                "gender_1"
                              )}
                            </td>
                            <td
                              className="width-66"
                              style={{
                                color: returnColor(
                                  calculateConsultationsHouseholdGenderSum(
                                    "gender_2"
                                  )
                                ),
                                fontWeight: returnFontWeight(
                                  calculateConsultationsHouseholdGenderSum(
                                    "gender_2"
                                  )
                                ),
                              }}
                            >
                              {calculateConsultationsHouseholdGenderSum(
                                "gender_2"
                              )}
                            </td>
                          </>
                        )}
                        {/* <td
                          className="width-126"
                          style={{
                            color: returnColor(
                              calculateConsultationsGenderSum(
                                "cancelled_consultations"
                              )
                            ),
                            fontWeight: returnFontWeight(
                              calculateConsultationsGenderSum(
                                "cancelled_consultations"
                              )
                            ),
                          }}
                        >
                          {calculateConsultationsGenderSum(
                            "cancelled_consultations"
                          )}
                        </td> */}
                      </RightTableLastLineItem3>
                    </tbody>
                  </table>
                </div>
              </TableLoadingWrapper>
              <TitlePopupWrapper2 style={{ justifyContent: "flex-end" }}>
                {/* <div className="title-popup">相談数</div> */}
                {/* <div className="title-text">
                  当月相談数合計：{totalConsultationsNotCancel}件
                </div> */}
                <div
                  className={`popup-btn${
                    !dashboardData?.result?.google_sheet_urls?.consultation
                      ? " disabled-btn"
                      : ""
                  }`}
                  onClick={() => {
                    if (
                      dashboardData?.result?.google_sheet_urls?.consultation
                    ) {
                      window.open(
                        dashboardData?.result?.google_sheet_urls?.consultation,
                        "_blank"
                      );
                    }
                  }}
                >
                  {loadingDashboard ? (
                    <Spin
                      indicator={
                        <LoadingOutlined
                          style={{ fontSize: 20, color: "white" }}
                          spin
                        />
                      }
                      size="small"
                      style={{ margin: "2px auto 0px 51px" }}
                    />
                  ) : (
                    <>
                      <PopUpIcon />
                      <div className="name">スプレッドシート</div>
                    </>
                  )}
                </div>
              </TitlePopupWrapper2>
            </TableContent3>
          ) : (
            <></>
          )}
        </TableWrapper>
      </WrapperDashboardContent>
    </React.Fragment>
  );
};

export default Dashboard4;
